import { API_CONFIG, AuthRequest, AuthResponse, Chat, ChatsListParams, ChatUpdate, ChatResponse, DashboardStats } from '../config/api';

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    
    // Проверяем, что baseUrl валидный
    if (!this.baseUrl || this.baseUrl === 'undefined') {
      console.error('API Base URL is not set or invalid:', this.baseUrl);
      throw new Error('API Base URL is not configured properly');
    }
    
    console.log('API Service initialized with base URL:', this.baseUrl);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    console.log('Making API request to:', url); // Добавляем логирование для отладки
    
    // Проверяем валидность URL
    try {
      new URL(url);
    } catch (error) {
      console.error('Invalid URL:', url);
      throw new Error(`Invalid API URL: ${url}`);
    }
    
    // Устанавливаем Content-Type только если body не FormData и не URLSearchParams
    const defaultHeaders: Record<string, string> = {};
    if (!(options.body instanceof FormData) && !(options.body instanceof URLSearchParams)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      // Включаем cookies для session-based авторизации
      credentials: 'include',
    };

    try {
      const response = await fetch(url, config);
      
      console.log('API response status:', response.status); // Добавляем логирование
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        
        // Обрабатываем ошибки авторизации и доступа
        if (response.status === 401 || response.status === 403) {
          throw new Error('Доступ разрешен только администрации безтендера.рф');
        }
        
        // Для других ошибок показываем общее сообщение
        throw new Error('Произошла ошибка при обращении к серверу');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async debugLogin(credentials: AuthRequest): Promise<AuthResponse> {
    // Отправляем данные как application/x-www-form-urlencoded
    const formData = new URLSearchParams();
    formData.append('login', credentials.login);
    formData.append('password', credentials.password);

    return this.request<AuthResponse>(API_CONFIG.ENDPOINTS.AUTH.DEBUG_LOGIN, {
      method: 'POST',
      body: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  // Альтернативный метод для отправки JSON (если понадобится)
  async debugLoginJson(credentials: AuthRequest): Promise<AuthResponse> {
    return this.request<AuthResponse>(API_CONFIG.ENDPOINTS.AUTH.DEBUG_LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // Telegram Widget авторизация
  async telegramWidgetLogin(dataCheckString: string): Promise<AuthResponse> {
    // Бэкенд ожидает data_check_string в заголовке Authorization: tw <data_check_string>
    // parse_qsl ожидает строку в формате query string (key1=value1&key2=value2)
    // Преобразуем строку с переносами строк в query string
    const queryString = dataCheckString.replace(/\n/g, '&');
    
    console.log('Telegram widget login - original data_check_string:', dataCheckString);
    console.log('Telegram widget login - query string format:', queryString);

    return this.request<AuthResponse>(API_CONFIG.ENDPOINTS.AUTH.TELEGRAM_WIDGET_LOGIN, {
      method: 'POST',
      headers: {
        'Authorization': `tw ${queryString}`,
      },
    });
  }

  // Получение списка чатов (требует авторизации)
  async getChats(params: ChatsListParams = {}): Promise<Chat[]> {
    const searchParams = new URLSearchParams();
    
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params.offset !== undefined) searchParams.append('offset', params.offset.toString());
    if (params.show_inactive !== undefined) searchParams.append('show_inactive', params.show_inactive.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${API_CONFIG.ENDPOINTS.CHATS.LIST}?${queryString}` : API_CONFIG.ENDPOINTS.CHATS.LIST;

    return this.request<Chat[]>(endpoint, {
      method: 'GET',
    });
  }

  // Получение конкретного чата по ID
  async getChat(chatId: number): Promise<ChatResponse> {
    return this.request<ChatResponse>(`${API_CONFIG.ENDPOINTS.CHATS.LIST}/${chatId}`, {
      method: 'GET',
    });
  }

  // Обновление чата
  async updateChat(chatId: number, data: ChatUpdate): Promise<ChatResponse> {
    return this.request<ChatResponse>(`${API_CONFIG.ENDPOINTS.CHATS.LIST}/${chatId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Выход из системы
  async logout(): Promise<void> {
    return this.request<void>(API_CONFIG.ENDPOINTS.AUTH.LOGOUT, {
      method: 'POST',
    });
  }

  // Получение статистики дэшборда
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>(API_CONFIG.ENDPOINTS.DASHBOARD.STATS, {
      method: 'GET',
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();
